import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ChevronLeft, ChevronRight, Eye, Database, 
  Server, HardDrive, Activity, AlertTriangle, CheckCircle, XCircle,
  ChevronUp, ChevronDown, ChevronsUpDown, Play, Pause, Users, EyeOff
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

const SqlServers = () => {
  const { canRead } = usePermissions('assets', 'sql');
  
  // Active view: 'servers' | 'databases' | 'jobs'
  const [activeView, setActiveView] = useState('servers');
  const [jobsEnabledFilter, setJobsEnabledFilter] = useState(null); // null = all, 1 = active, 0 = inactive
  
  // Stats
  const [stats, setStats] = useState({ total_instances: 0, total_databases: 0, active_jobs: 0, inactive_jobs: 0, total_size_gb: 0 });
  
  // Servers view state
  const [sqlServers, setSqlServers] = useState([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [serversPage, setServersPage] = useState(1);
  const [serversTotalPages, setServersTotalPages] = useState(1);
  const [serversTotal, setServersTotal] = useState(0);
  const [serversFilters, setServersFilters] = useState({ hostname: '', edition: '' });
  const [serversFilterInput, setServersFilterInput] = useState(''); // Input inmediato
  const [serversSortConfig, setServersSortConfig] = useState({ field: 'hostname', dir: 'asc' });
  const [serversFilterOptions, setServersFilterOptions] = useState({ editions: [] });
  const [showIgnored, setShowIgnored] = useState(false); // Toggle para mostrar ignoradas
  
  // Databases view state
  const [databases, setDatabases] = useState([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbPage, setDbPage] = useState(1);
  const [dbTotalPages, setDbTotalPages] = useState(1);
  const [dbTotal, setDbTotal] = useState(0);
  const [dbFilters, setDbFilters] = useState({ search: '', server: '', state: '' });
  const [dbFilterInput, setDbFilterInput] = useState(''); // Input inmediato para BD
  const [dbServerFilterInput, setDbServerFilterInput] = useState(''); // Input inmediato para servidor
  const [dbSortConfig, setDbSortConfig] = useState({ field: 'database_name', dir: 'asc' });
  
  // Jobs view state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsTotalPages, setJobsTotalPages] = useState(1);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsFilters, setJobsFilters] = useState({ search: '', server: '', status: '' });
  const [jobsFilterInput, setJobsFilterInput] = useState(''); // Input inmediato para job
  const [jobsServerFilterInput, setJobsServerFilterInput] = useState(''); // Input inmediato para servidor
  const [jobsSortConfig, setJobsSortConfig] = useState({ field: 'job_name', dir: 'asc' });
  const [pageSize, setPageSize] = useState(25);

  // Modal de usuarios de BD
  const [dbUsersModal, setDbUsersModal] = useState({ open: false, dbName: '', serverName: '', users: [], loading: false });

  // Debounce para filtros de texto (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (serversFilterInput !== serversFilters.hostname) {
        setServersFilters(prev => ({ ...prev, hostname: serversFilterInput }));
        setServersPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [serversFilterInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (dbFilterInput !== dbFilters.search) {
        setDbFilters(prev => ({ ...prev, search: dbFilterInput }));
        setDbPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [dbFilterInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (dbServerFilterInput !== dbFilters.server) {
        setDbFilters(prev => ({ ...prev, server: dbServerFilterInput }));
        setDbPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [dbServerFilterInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (jobsFilterInput !== jobsFilters.search) {
        setJobsFilters(prev => ({ ...prev, search: jobsFilterInput }));
        setJobsPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [jobsFilterInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (jobsServerFilterInput !== jobsFilters.server) {
        setJobsFilters(prev => ({ ...prev, server: jobsServerFilterInput }));
        setJobsPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [jobsServerFilterInput]);

  // Sort icon component
  const SortIcon = ({ field, currentSort }) => {
    if (currentSort.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return currentSort.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // ============ Fetch Functions ============
  
  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/sql/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSqlServers = async () => {
    setServersLoading(true);
    try {
      const params = new URLSearchParams({
        page: serversPage.toString(),
        page_size: pageSize.toString(),
        sort_by: serversSortConfig.field,
        sort_dir: serversSortConfig.dir,
      });
      if (serversFilters.hostname) params.append('search', serversFilters.hostname);
      if (serversFilters.edition) params.append('edition', serversFilters.edition);
      if (showIgnored) params.append('show_ignored', 'true');

      const response = await api.get(`/assets/sql?${params}`);
      setSqlServers(response.data.items);
      setServersTotalPages(response.data.pages);
      setServersTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar SQL Servers');
      console.error(error);
    } finally {
      setServersLoading(false);
    }
  };

  const fetchServersFilterOptions = async () => {
    try {
      const response = await api.get('/assets/sql/filter/options');
      setServersFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchDatabases = async () => {
    setDbLoading(true);
    try {
      const params = new URLSearchParams({
        page: dbPage.toString(),
        page_size: pageSize.toString(),
        sort_by: dbSortConfig.field,
        sort_dir: dbSortConfig.dir,
      });
      if (dbFilters.search) params.append('search', dbFilters.search);
      if (dbFilters.server) params.append('server_filter', dbFilters.server);
      if (dbFilters.state && dbFilters.state !== 'all') params.append('state_filter', dbFilters.state);

      const response = await api.get(`/assets/sql/databases/all?${params}`);
      setDatabases(response.data.items);
      setDbTotalPages(response.data.pages);
      setDbTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar bases de datos');
      console.error(error);
    } finally {
      setDbLoading(false);
    }
  };

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const params = new URLSearchParams({
        page: jobsPage.toString(),
        page_size: pageSize.toString(),
        sort_by: jobsSortConfig.field,
        sort_dir: jobsSortConfig.dir,
      });
      if (jobsFilters.search) params.append('search', jobsFilters.search);
      if (jobsFilters.server) params.append('server_filter', jobsFilters.server);
      if (jobsFilters.status && jobsFilters.status !== 'all') params.append('status_filter', jobsFilters.status);
      if (jobsEnabledFilter !== null) params.append('enabled_filter', jobsEnabledFilter.toString());

      const response = await api.get(`/assets/sql/jobs/all?${params}`);
      setJobs(response.data.items);
      setJobsTotalPages(response.data.pages);
      setJobsTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar jobs');
      console.error(error);
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchDbUsers = async (instanceId, dbName, serverName) => {
    setDbUsersModal({ open: true, dbName, serverName, users: [], loading: true });
    try {
      const response = await api.get(`/assets/sql/${instanceId}/db-usuarios?db_name=${encodeURIComponent(dbName)}`);
      setDbUsersModal(prev => ({ ...prev, users: response.data.items || [], loading: false }));
    } catch (error) {
      console.error('Error fetching DB users:', error);
      setDbUsersModal(prev => ({ ...prev, users: [], loading: false }));
      toast.error('Error al cargar usuarios de la BD');
    }
  };

  // ============ Effects ============

  useEffect(() => {
    fetchStats();
    fetchServersFilterOptions();
  }, []);

  useEffect(() => {
    if (activeView === 'servers') {
      fetchSqlServers();
    }
  }, [activeView, serversPage, serversFilters, serversSortConfig, showIgnored]);

  useEffect(() => {
    if (activeView === 'databases') {
      fetchDatabases();
    }
  }, [activeView, dbPage, dbFilters, dbSortConfig]);

  useEffect(() => {
    if (activeView === 'jobs') {
      fetchJobs();
    }
  }, [activeView, jobsPage, jobsFilters, jobsSortConfig, jobsEnabledFilter]);

  // ============ Handlers ============

  const handleStatClick = (view, enabledFilter = null) => {
    if (view === 'jobs') {
      setJobsEnabledFilter(enabledFilter);
      setJobsPage(1);
      setJobsFilters({ search: '', server: '', status: '' });
      setJobsFilterInput('');
      setJobsServerFilterInput('');
    }
    if (view === 'databases') {
      setDbPage(1);
      setDbFilters({ search: '', server: '', state: '' });
      setDbFilterInput('');
      setDbServerFilterInput('');
    }
    if (view === 'servers') {
      setServersPage(1);
      setServersFilters({ hostname: '', edition: '' });
      setServersFilterInput('');
    }
    setActiveView(view);
  };

  const handleServersSort = (field) => {
    setServersSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDbSort = (field) => {
    setDbSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleJobsSort = (field) => {
    setJobsSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // ============ Format Helpers ============

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver SQL Servers</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">SQL Servers</h1>
            <p className="page-subtitle">
              {activeView === 'servers' && 'Motores de base de datos SQL Server'}
              {activeView === 'databases' && 'Todas las bases de datos'}
              {activeView === 'jobs' && (jobsEnabledFilter === 1 ? 'Jobs Activos' : jobsEnabledFilter === 0 ? 'Jobs Inactivos' : 'Todos los Jobs')}
            </p>
          </div>
        </div>

        {/* Stats - Clickeable */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="sql-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeView === 'servers' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatClick('servers')}
            data-testid="stats-total"
          >
            <Server className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total_instances}</div>
              <div className="stats-card-label">Instancias</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${activeView === 'databases' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleStatClick('databases')}
            data-testid="stats-databases"
          >
            <Database className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.total_databases}</div>
              <div className="stats-card-label">Bases de Datos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeView === 'jobs' && jobsEnabledFilter === 1 ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatClick('jobs', 1)}
            data-testid="stats-jobs-active"
          >
            <Play className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.active_jobs}</div>
              <div className="stats-card-label">Jobs Activos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${activeView === 'jobs' && jobsEnabledFilter === 0 ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatClick('jobs', 0)}
            data-testid="stats-jobs-inactive"
          >
            <Pause className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.inactive_jobs}</div>
              <div className="stats-card-label">Jobs Inactivos</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-size">
            <HardDrive className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-purple-500">
                {stats.total_size_gb >= 1024 
                  ? `${(stats.total_size_gb / 1024).toFixed(1)} TB` 
                  : `${stats.total_size_gb} GB`}
              </div>
              <div className="stats-card-label">Tamaño Total</div>
            </div>
          </div>
        </div>

        {/* ============ SERVERS VIEW ============ */}
        {activeView === 'servers' && (
          <Card className="card-subtle overflow-hidden">
            <CardContent className="p-0">
              {serversLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleServersSort('hostname')}>
                            <div className="flex items-center gap-1">INSTANCIA <SortIcon field="hostname" currentSort={serversSortConfig} /></div>
                          </th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleServersSort('ip')}>
                            <div className="flex items-center gap-1">IP <SortIcon field="ip" currentSort={serversSortConfig} /></div>
                          </th>
                          <th>EDICION</th>
                          <th className="text-center cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleServersSort('db_count')}>
                            <div className="flex items-center justify-center gap-1">DATABASES <SortIcon field="db_count" currentSort={serversSortConfig} /></div>
                          </th>
                          <th className="text-center cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleServersSort('job_count')}>
                            <div className="flex items-center justify-center gap-1">JOBS <SortIcon field="job_count" currentSort={serversSortConfig} /></div>
                          </th>
                          <th className="text-right cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleServersSort('total_size_gb')}>
                            <div className="flex items-center justify-end gap-1">TAMAÑO <SortIcon field="total_size_gb" currentSort={serversSortConfig} /></div>
                          </th>
                          <th className="w-20 text-center">ACCIONES</th>
                        </tr>
                        <tr className="bg-muted/30">
                          <th className="py-2 px-4">
                            <Input
                              placeholder="Filtrar..."
                              value={serversFilterInput}
                              onChange={(e) => setServersFilterInput(e.target.value)}
                              className="h-7 text-xs bg-background"
                            />
                          </th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4">
                            <Select 
                              value={serversFilters.edition || "all"} 
                              onValueChange={(v) => {
                                setServersFilters(prev => ({ ...prev, edition: v === "all" ? "" : v }));
                                setServersPage(1);
                              }}
                            >
                              <SelectTrigger className="h-7 text-xs bg-background">
                                <SelectValue placeholder="Todas" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todas</SelectItem>
                                {serversFilterOptions.editions.map(e => (
                                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                          <th className="w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sqlServers.length === 0 ? (
                          <tr>
                            <td colSpan={7}>
                              <div className="empty-state">
                                <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                <p className="empty-state-text">No se encontraron SQL Servers</p>
                              </div>
                            </td>
                          </tr>
                        ) : sqlServers.map((srv) => (
                          <tr key={srv.id} className={srv.is_ignored ? 'opacity-50 bg-muted/20' : ''}>
                            <td>
                              <div className="flex items-center gap-2">
                                <Link to={`/assets/sql/${srv.id}`} className="font-medium text-primary hover:underline">
                                  {srv.display_name || srv.hostname}
                                </Link>
                                {srv.instance_name && (
                                  <span className="text-xs text-muted-foreground">({srv.instance_name})</span>
                                )}
                                {srv.is_ignored && (
                                  <span 
                                    className="badge badge-outline text-xs inline-flex items-center gap-1" 
                                    title={srv.ignored_reason ? `Motivo: ${srv.ignored_reason}\n${srv.ignored_at ? `Desde: ${formatDateARG(srv.ignored_at)}` : ''}` : 'Ignorada'}
                                  >
                                    <EyeOff className="h-3 w-3" /> Ignorada
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="font-mono text-sm text-muted-foreground">{srv.ip || '-'}</td>
                            <td className="text-muted-foreground">{srv.sql_edition || srv.edition || '-'}</td>
                            <td className="text-center"><span className="badge badge-info">{srv.db_count}</span></td>
                            <td className="text-center"><span className="badge badge-success">{srv.job_count}</span></td>
                            <td className="text-right font-mono text-sm text-muted-foreground">{srv.total_size_gb} GB</td>
                            <td className="text-center">
                              <Link to={`/assets/sql/${srv.id}`}>
                                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted"><Eye className="h-4 w-4" /></Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {serversTotalPages > 0 && (
                    <div className="flex items-center justify-between p-4 border-t border-border">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">
                          {serversTotal > 0 ? `${((serversPage - 1) * pageSize) + 1} - ${Math.min(serversPage * pageSize, serversTotal)} de ${serversTotal}` : '0 registros'}
                        </span>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={showIgnored}
                            onChange={(e) => { setShowIgnored(e.target.checked); setServersPage(1); }}
                            className="h-4 w-4 rounded border-border"
                          />
                          <EyeOff className="h-3.5 w-3.5" />
                          Mostrar ignoradas
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setServersPage(p => Math.max(1, p - 1))} disabled={serversPage === 1} className="h-8 w-8 p-0">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">{serversPage} / {serversTotalPages || 1}</span>
                        <Button variant="outline" size="sm" onClick={() => setServersPage(p => Math.min(serversTotalPages, p + 1))} disabled={serversPage >= serversTotalPages} className="h-8 w-8 p-0">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ============ DATABASES VIEW ============ */}
        {activeView === 'databases' && (
          <Card className="card-subtle overflow-hidden">
            <CardContent className="p-0">
              {dbLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleDbSort('hostname')}>
                            <div className="flex items-center gap-1">INSTANCIA <SortIcon field="hostname" currentSort={dbSortConfig} /></div>
                          </th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleDbSort('database_name')}>
                            <div className="flex items-center gap-1">BASE DE DATOS <SortIcon field="database_name" currentSort={dbSortConfig} /></div>
                          </th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleDbSort('state')}>
                            <div className="flex items-center gap-1">ESTADO <SortIcon field="state" currentSort={dbSortConfig} /></div>
                          </th>
                          <th>RECOVERY</th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none text-right" onClick={() => handleDbSort('size_mb')}>
                            <div className="flex items-center justify-end gap-1">TAMAÑO <SortIcon field="size_mb" currentSort={dbSortConfig} /></div>
                          </th>
                          <th className="text-right">DATA</th>
                          <th className="text-right">LOG</th>
                          <th className="text-center">COMPAT</th>
                          <th className="w-16"></th>
                        </tr>
                        <tr className="bg-muted/30">
                          <th className="py-2 px-4">
                            <Input
                              placeholder="Filtrar servidor/instancia..."
                              value={dbServerFilterInput}
                              onChange={(e) => setDbServerFilterInput(e.target.value)}
                              className="h-7 text-xs bg-background"
                            />
                          </th>
                          <th className="py-2 px-4">
                            <Input
                              placeholder="Filtrar BD..."
                              value={dbFilterInput}
                              onChange={(e) => setDbFilterInput(e.target.value)}
                              className="h-7 text-xs bg-background"
                            />
                          </th>
                          <th className="py-2 px-4">
                            <Select value={dbFilters.state || "all"} onValueChange={(v) => { setDbFilters(prev => ({ ...prev, state: v })); setDbPage(1); }}>
                              <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="ONLINE">ONLINE</SelectItem>
                                <SelectItem value="OFFLINE">OFFLINE</SelectItem>
                                <SelectItem value="RESTORING">RESTORING</SelectItem>
                              </SelectContent>
                            </Select>
                          </th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {databases.length === 0 ? (
                          <tr>
                            <td colSpan={9}>
                              <div className="empty-state">
                                <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                <p className="empty-state-text">No se encontraron bases de datos</p>
                              </div>
                            </td>
                          </tr>
                        ) : databases.map((db) => (
                          <tr key={db.id}>
                            <td>
                              <Link to={`/assets/sql/${db.instance_id || db.server_id}`} className="font-medium text-primary hover:underline">
                                {db.display_name || db.hostname}
                              </Link>
                            </td>
                            <td className="font-medium">{db.database_name}</td>
                            <td>
                              <span className={`badge ${db.state === 'ONLINE' ? 'badge-success' : db.state === 'OFFLINE' ? 'badge-error' : 'badge-warning'}`}>
                                {db.state}
                              </span>
                            </td>
                            <td>
                              <span className="badge badge-outline">{db.recovery_model || '-'}</span>
                            </td>
                            <td className="text-right font-mono text-sm">
                              {db.size_mb >= 1024 ? `${(db.size_mb / 1024).toFixed(1)} GB` : `${(db.size_mb || 0).toFixed(0)} MB`}
                            </td>
                            <td className="text-right font-mono text-sm text-muted-foreground">
                              {db.data_size_mb >= 1024 ? `${(db.data_size_mb / 1024).toFixed(1)} GB` : `${(db.data_size_mb || 0).toFixed(0)} MB`}
                            </td>
                            <td className="text-right font-mono text-sm text-muted-foreground">
                              {db.log_size_mb >= 1024 ? `${(db.log_size_mb / 1024).toFixed(1)} GB` : `${(db.log_size_mb || 0).toFixed(0)} MB`}
                            </td>
                            <td className="text-center text-muted-foreground">{db.compatibility_level || '-'}</td>
                            <td className="text-center">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 hover:bg-muted"
                                onClick={() => fetchDbUsers(db.instance_id || db.server_id, db.database_name, db.display_name || db.hostname)}
                                title="Ver usuarios de esta BD"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {dbTotalPages > 0 && (
                    <div className="flex items-center justify-between p-4 border-t border-border">
                      <div className="text-sm text-muted-foreground">
                        {dbTotal > 0 ? `${((dbPage - 1) * pageSize) + 1} - ${Math.min(dbPage * pageSize, dbTotal)} de ${dbTotal}` : '0 registros'}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setDbPage(p => Math.max(1, p - 1))} disabled={dbPage === 1} className="h-8 w-8 p-0">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">{dbPage} / {dbTotalPages || 1}</span>
                        <Button variant="outline" size="sm" onClick={() => setDbPage(p => Math.min(dbTotalPages, p + 1))} disabled={dbPage >= dbTotalPages} className="h-8 w-8 p-0">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ============ JOBS VIEW ============ */}
        {activeView === 'jobs' && (
          <Card className="card-subtle overflow-hidden">
            <CardContent className="p-0">
              {jobsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleJobsSort('hostname')}>
                            <div className="flex items-center gap-1">INSTANCIA <SortIcon field="hostname" currentSort={jobsSortConfig} /></div>
                          </th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleJobsSort('job_name')}>
                            <div className="flex items-center gap-1">JOB <SortIcon field="job_name" currentSort={jobsSortConfig} /></div>
                          </th>
                          <th className="text-center">ESTADO</th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleJobsSort('last_run_status')}>
                            <div className="flex items-center gap-1">ÚLTIMO RESULTADO <SortIcon field="last_run_status" currentSort={jobsSortConfig} /></div>
                          </th>
                          <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleJobsSort('last_run_date')}>
                            <div className="flex items-center gap-1">ÚLTIMA EJECUCIÓN <SortIcon field="last_run_date" currentSort={jobsSortConfig} /></div>
                          </th>
                          <th>DURACIÓN</th>
                        </tr>
                        <tr className="bg-muted/30">
                          <th className="py-2 px-4">
                            <Input
                              placeholder="Filtrar servidor/instancia..."
                              value={jobsServerFilterInput}
                              onChange={(e) => setJobsServerFilterInput(e.target.value)}
                              className="h-7 text-xs bg-background"
                            />
                          </th>
                          <th className="py-2 px-4">
                            <Input
                              placeholder="Filtrar job..."
                              value={jobsFilterInput}
                              onChange={(e) => setJobsFilterInput(e.target.value)}
                              className="h-7 text-xs bg-background"
                            />
                          </th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4">
                            <Select value={jobsFilters.status || "all"} onValueChange={(v) => { setJobsFilters(prev => ({ ...prev, status: v })); setJobsPage(1); }}>
                              <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="Succeeded">Succeeded</SelectItem>
                                <SelectItem value="Failed">Failed</SelectItem>
                                <SelectItem value="Cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </th>
                          <th className="py-2 px-4"></th>
                          <th className="py-2 px-4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobs.length === 0 ? (
                          <tr>
                            <td colSpan={6}>
                              <div className="empty-state">
                                <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                <p className="empty-state-text">No se encontraron jobs</p>
                              </div>
                            </td>
                          </tr>
                        ) : jobs.map((job) => (
                          <tr key={job.id}>
                            <td>
                              <Link to={`/assets/sql/${job.instance_id || job.server_id}`} className="font-medium text-primary hover:underline">
                                {job.display_name || job.hostname}
                              </Link>
                            </td>
                            <td className="font-medium max-w-[300px] truncate" title={job.job_name}>{job.job_name}</td>
                            <td className="text-center">
                              {job.enabled ? (
                                <span className="badge badge-success inline-flex items-center gap-1"><Play className="h-3 w-3" /> Activo</span>
                              ) : (
                                <span className="badge badge-warning inline-flex items-center gap-1"><Pause className="h-3 w-3" /> Inactivo</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${job.last_run_status === 'Succeeded' ? 'badge-success' : job.last_run_status === 'Failed' ? 'badge-error' : 'badge-default'}`}>
                                {job.last_run_status || '-'}
                              </span>
                            </td>
                            <td className="text-muted-foreground text-sm">{formatDate(job.last_run_date)}</td>
                            <td className="text-muted-foreground text-sm font-mono">{formatDuration(job.last_run_duration)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {jobsTotalPages > 0 && (
                    <div className="flex items-center justify-between p-4 border-t border-border">
                      <div className="text-sm text-muted-foreground">
                        {jobsTotal > 0 ? `${((jobsPage - 1) * pageSize) + 1} - ${Math.min(jobsPage * pageSize, jobsTotal)} de ${jobsTotal}` : '0 registros'}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setJobsPage(p => Math.max(1, p - 1))} disabled={jobsPage === 1} className="h-8 w-8 p-0">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">{jobsPage} / {jobsTotalPages || 1}</span>
                        <Button variant="outline" size="sm" onClick={() => setJobsPage(p => Math.min(jobsTotalPages, p + 1))} disabled={jobsPage >= jobsTotalPages} className="h-8 w-8 p-0">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modal de Usuarios de BD */}
        <Dialog open={dbUsersModal.open} onOpenChange={(open) => setDbUsersModal(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Usuarios de {dbUsersModal.dbName}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">{dbUsersModal.serverName}</p>
            </DialogHeader>
            {dbUsersModal.loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : dbUsersModal.users.length > 0 ? (
              <div className="overflow-x-auto max-h-96">
                <table className="table-subtle text-sm">
                  <thead>
                    <tr>
                      <th>USUARIO</th>
                      <th>TIPO</th>
                      <th>ESTADO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbUsersModal.users.map((user, idx) => (
                      <tr key={idx}>
                        <td className="font-medium">{user.user_name || user.username}</td>
                        <td className="text-muted-foreground">{user.user_type || user.type_desc || '-'}</td>
                        <td>
                          <span className={`badge ${user.is_disabled ? 'badge-error' : 'badge-success'}`}>
                            {user.is_disabled ? 'Deshabilitado' : 'Activo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No hay usuarios en esta base de datos</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SqlServers;
